import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CustomGraphLegend } from '../../../../SharedComponents/CustomGraphLegend';
import { CustomGraphTooltip } from '../../../../SharedComponents/CustomGraphTooltip';
import { GraphWrapper } from '../../../../SharedComponents/GraphWrapper';
import { MonthAndYearCustomTick } from '../../../../SharedComponents/MonthAndYearCustomTick';
import { format } from 'date-fns';
import { useCallback } from 'react';
import { formatStackedNumbers } from '../../../../../utils';
import { formatLocalDate } from '../../../../../utils/localDateToDate';
import type { AnalyticsNumBedroomType } from '@zg-rentals/ts-customer-analytics-client';
import { useMainLayout } from '../../../../layouts/MainPageLayout/MainLayoutContext';
import { formatCurrency } from '../../../../../utils/formatCurrency';
import { roundUp } from '../../../../../utils/roundUp';
import { analyticsNumBedroomTypeToLabelMapping, radioButtonEnum } from '../MarketDataReport.utils';
import type { RentPriceChartData } from '../MarketDataReport.data';
import { getGraphTitle } from '../../../../../utils/getGraphTitle';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { emitMarketDataChooseBedFilterEvent } from '../../../../../analytics/events';

type SingleFamilyRentGraphProps = {
  selectedRegionId: number;
  chartData: Array<RentPriceChartData>;
  isLoading: boolean;
  bedsFilter: AnalyticsNumBedroomType;
  setBedsFilter: (value: AnalyticsNumBedroomType) => void;
};

export const SingleFamilyRentGraph = ({
  selectedRegionId,
  chartData,
  isLoading,
  bedsFilter,
  setBedsFilter,
}: SingleFamilyRentGraphProps) => {
  const { user } = useRentalManagerUser();
  const { filteredProperties, regionsMapping, selectedFilters, isFetchingInitialData, analyticsLastUpdatedDates } =
    useMainLayout();
  const lastUpdatedPortfolioDate = formatLocalDate(analyticsLastUpdatedDates?.portfolioRentLastUpdated);
  const lastUpdatedMarketDate = formatLocalDate(analyticsLastUpdatedDates?.marketRentLastUpdated);
  const handleBedsFilterChange = useCallback(
    (value: AnalyticsNumBedroomType) => {
      if (user) emitMarketDataChooseBedFilterEvent({ user });
      setBedsFilter(value);
    },
    [user, setBedsFilter],
  );

  const propertiesForGraph = filteredProperties.filter((property) => property.aggregateStats.isSingleFamily);

  const currentDateFormatted = format(new Date(), 'MMM yyyy');
  const mtdData = chartData.find((data) => data.name === currentDateFormatted);
  const portfolioAverage = mtdData?.['Portfolio average month to date'] ?? mtdData?.['Portfolio average'] ?? 0;
  const marketAverage = mtdData?.['Market average month to date'] ?? mtdData?.['Market average'] ?? 0;
  const bedsFilterLabel = analyticsNumBedroomTypeToLabelMapping[bedsFilter];

  const title = getGraphTitle({
    graphName: 'Single-family rent',
    regionsMapping,
    selectedRegionId,
    selectedFilters,
    filteredProperties,
  });

  if (propertiesForGraph.length === 0) return null;

  return (
    <GraphWrapper
      graphId="single-family-rent"
      title={title}
      subtitle={`The average rent for your single-family portfolio properties compared to the market average.${lastUpdatedPortfolioDate && lastUpdatedMarketDate ? ` Portfolio data updated ${lastUpdatedPortfolioDate}, market data updated ${lastUpdatedMarketDate}.` : ''}`}
      loading={isLoading || isFetchingInitialData}
      radioButtonFilter={bedsFilter}
      setRadioButtonFilter={handleBedsFilterChange}
      radioButtonEnum={radioButtonEnum}
      radioButtonEnumLabelMapping={analyticsNumBedroomTypeToLabelMapping}
      infoCards={[
        {
          title: 'Portfolio average',
          infoText: `Average single-family rent for your portfolio in the selected market for ${bedsFilterLabel}, month to date.`,
          value: formatCurrency(roundUp(portfolioAverage, 0)),
        },
        {
          title: 'Market average',
          infoText: `Average single-family rent for the selected market for ${bedsFilterLabel}, month to date.`,
          value: formatCurrency(roundUp(marketAverage, 0)),
        },
      ]}
    >
      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="timestamp" interval={0} tick={<MonthAndYearCustomTick />} />
          <YAxis tickFormatter={(value) => `$${formatStackedNumbers(value)}`} />
          <Tooltip
            formatter={(value) => formatCurrency(Number(value))}
            content={
              <CustomGraphTooltip
                filterPayload={
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (filteredPayload: Array<any>, displayLabel: string) => {
                    const isCurrentMonth = displayLabel === currentDateFormatted;

                    if (isCurrentMonth) {
                      return filteredPayload.filter(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (entry: any) =>
                          entry.dataKey === 'Portfolio average month to date' ||
                          entry.dataKey === 'Market average month to date',
                      );
                    }

                    return filteredPayload.filter(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (entry: any) => entry.dataKey === 'Portfolio average' || entry.dataKey === 'Market average',
                    );
                  }
                }
              />
            }
          />
          <Legend
            wrapperStyle={{ marginBottom: '-32px' }}
            content={
              <CustomGraphLegend
                labelTypes={{
                  'Portfolio average': 'line',
                  'Market average': 'line',
                  'Month to date': 'dashed-line',
                }}
                filterPayload={
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (payload: Array<any>) => {
                    // Filter out individual month-to-date entries
                    const filtered = payload.filter(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (entry: any) =>
                        entry.value !== 'Portfolio average month to date' &&
                        entry.value !== 'Market average month to date',
                    );
                    // Reorder to: Portfolio average, Market average, Month to date
                    const order = ['Portfolio average', 'Market average', 'Month to date'];
                    return filtered.sort(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (a: any, b: any) => order.indexOf(a.value) - order.indexOf(b.value),
                    );
                  }
                }
              />
            }
          />
          <Line
            type="linear"
            dataKey="Portfolio average month to date"
            stroke="#891EFA"
            strokeWidth={3}
            strokeDasharray="5 5"
            dot={false}
            legendType="none"
          />
          <Line
            type="linear"
            dataKey="Market average month to date"
            stroke="#009AAE"
            strokeWidth={3}
            strokeDasharray="5 5"
            dot={false}
            legendType="none"
          />
          <Line type="linear" dataKey="Month to date" stroke="#666666" strokeDasharray="5 5" dot={false} />
          <Line type="linear" dataKey="Portfolio average" stroke="#891EFA" strokeWidth={3} />
          <Line type="linear" dataKey="Market average" stroke="#009AAE" strokeWidth={3} />
        </LineChart>
      </ResponsiveContainer>
    </GraphWrapper>
  );
};
